import React, { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';

/**
 * EventPayloadDrawer — Shows raw JSON event_data for a selected event.
 *
 * Props:
 *   event: { id, event_name, event_data, created_at, ... }
 *   onClose: () => void
 */

const EventPayloadDrawer = ({ event, onClose }) => {
    const [copied, setCopied] = useState(false);

    if (!event) return null;

    const payloadStr = JSON.stringify(event.event_data || {}, null, 2);

    const handleCopy = () => {
        navigator.clipboard.writeText(payloadStr).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <>
            <div onClick={onClose} style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)',
                zIndex: 100,
            }} />
            <div style={{
                position: 'fixed', top: 0, right: 0, bottom: 0,
                width: 480, maxWidth: '90vw',
                background: '#fff', zIndex: 101,
                boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
                display: 'flex', flexDirection: 'column',
                animation: 'slideInRight 0.2s ease-out',
            }}>
                {/* Header */}
                <div style={{
                    padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div style={{ minWidth: 0 }}>
                        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: 0 }}>
                            {event.event_name || 'Event Detail'}
                        </h3>
                        <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>
                            {event.created_at ? new Date(event.created_at).toLocaleString() : ''}
                        </p>
                    </div>
                    <button onClick={onClose} style={{
                        padding: 6, borderRadius: 8, border: 'none',
                        background: '#f1f5f9', cursor: 'pointer', color: '#64748b',
                    }}>
                        <X size={16} />
                    </button>
                </div>

                {/* Meta fields */}
                <div style={{ padding: '12px 20px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        {[
                            ['Category', event.event_category],
                            ['User UUID', event.user_uuid],
                            ['Session ID', event.session_id],
                            ['Request ID', event.request_id],
                            ['Route', event.route],
                            ['Feature Area', event.feature_area],
                            ['Deployment', event.deployment_version],
                            ['District', event.district_id],
                            ['Campus', event.campus_id],
                        ].filter(([, v]) => v).map(([label, value]) => (
                            <div key={label}>
                                <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                                    {label}
                                </div>
                                <div style={{
                                    fontSize: 12, color: '#374151', fontFamily: 'monospace',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {value}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Payload */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        marginBottom: 8,
                    }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                            Event Payload
                        </span>
                        <button
                            onClick={handleCopy}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                fontSize: 11, color: copied ? '#10b981' : '#6366f1',
                                background: 'none', border: 'none', cursor: 'pointer',
                            }}
                        >
                            {copied ? <Check size={12} /> : <Copy size={12} />}
                            {copied ? 'Copied' : 'Copy'}
                        </button>
                    </div>
                    <pre style={{
                        background: '#0f172a', color: '#e2e8f0',
                        padding: 16, borderRadius: 10, fontSize: 12,
                        fontFamily: '"SF Mono", "Fira Code", Consolas, monospace',
                        overflow: 'auto', lineHeight: 1.5,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                        margin: 0,
                    }}>
                        {payloadStr}
                    </pre>
                </div>
            </div>
            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); }
                    to { transform: translateX(0); }
                }
            `}</style>
        </>
    );
};

export default EventPayloadDrawer;
